import { useCallback, useEffect, useRef, useState } from "react";

const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
export const speechSupported = !!SR;
const isMobile = typeof navigator !== "undefined" && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
const FLUSH_MS = 600;
const MERGE_WINDOW_MS = 8000;
const RESTART_DELAY_MS = 120;

function splitResults(e) {
  const finals = [];
  let interim = "";
  for (let i = e.resultIndex; i < e.results.length; i++) {
    const r = e.results[i];
    const t = (r[0]?.transcript || "").trim();
    if (!t) continue;
    if (r.isFinal) finals.push(t);
    else interim = t;
  }
  return { finals, interim };
}

export function useSpeechRecognition({ lang, onFinal, onInterim }) {
  const [listening, setListening] = useState(false);
  const [error, setError] = useState(null);
  const recRef = useRef(null);
  const wantRef = useRef(false);
  const pendingRef = useRef({ text: "", timer: null });
  const lastFinalRef = useRef({ text: "", at: 0 });
  const cbRef = useRef({ onFinal, onInterim });
  cbRef.current = { onFinal, onInterim };

  const flush = useCallback(() => {
    const p = pendingRef.current;
    clearTimeout(p.timer);
    const text = p.text;
    pendingRef.current = { text: "", timer: null };
    if (!text) return;
    const now = Date.now();
    const last = lastFinalRef.current;
    const recent = now - last.at < MERGE_WINDOW_MS;
    if (recent && (text === last.text || last.text.startsWith(text))) return;
    const replace = recent && !!last.text && text.startsWith(last.text);
    lastFinalRef.current = { text, at: now };
    cbRef.current.onInterim?.("");
    cbRef.current.onFinal?.(text, replace);
  }, []);

  const queueFinal = useCallback(
    (t) => {
      const p = pendingRef.current;
      if (p.text) {
        if (t === p.text || p.text.startsWith(t)) return;
        if (!t.startsWith(p.text)) flush();
      }
      pendingRef.current.text = t;
      cbRef.current.onInterim?.(t);
      clearTimeout(pendingRef.current.timer);
      pendingRef.current.timer = setTimeout(flush, FLUSH_MS);
    },
    [flush]
  );

  const stop = useCallback(() => {
    wantRef.current = false;
    recRef.current?.stop();
    recRef.current = null;
    flush();
    setListening(false);
    cbRef.current.onInterim?.("");
  }, [flush]);

  const start = useCallback(() => {
    if (!SR) {
      setError("unsupported");
      return;
    }
    if (recRef.current) return;
    const rec = new SR();
    rec.lang = lang;
    rec.continuous = !isMobile;
    rec.interimResults = true;
    rec.maxAlternatives = 1;
    rec.onresult = (e) => {
      const { finals, interim } = splitResults(e);
      finals.forEach(queueFinal);
      if (interim) cbRef.current.onInterim?.(pendingRef.current.text ? `${pendingRef.current.text} ${interim}` : interim);
    };
    rec.onerror = (e) => {
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        setError("not-allowed");
        wantRef.current = false;
        setListening(false);
      }
    };
    rec.onend = () => {
      recRef.current = null;
      flush();
      cbRef.current.onInterim?.("");
      if (wantRef.current) setTimeout(() => wantRef.current && start(), RESTART_DELAY_MS);
      else setListening(false);
    };
    recRef.current = rec;
    wantRef.current = true;
    setError(null);
    try {
      rec.start();
      setListening(true);
    } catch {
      recRef.current = null;
    }
  }, [lang, flush, queueFinal]);

  const toggle = useCallback(() => (wantRef.current ? stop() : start()), [start, stop]);

  useEffect(() => () => stop(), [stop]);

  return { listening, error, start, stop, toggle, supported: speechSupported };
}
